<!-- ProcessDesign.vue -->
<template>
    <div id="app">
        <div class="header-row">
            <!-- 标题居中容器 -->
            <div class="header-center">
                <h1>工艺流程设计工具</h1>
            </div>
            <!-- 返回首页按钮 - 放在标题右侧 -->
            <i class="el-icon-s-home backhome-icon" @click="backHome()"></i>
        </div>
        
        <div class="container">
            <!-- 左侧工艺基础数据列表 -->
            <div class="sidebar">
                <h3>工艺基础数据</h3>
                <div v-if="loading">加载中...</div>
                <div v-else>
                    <div 
                        v-for="(item, index) in processData" 
                        :key="index"
                        class="process-item"
                        draggable="true"
                        @dragstart="onDragStart($event, item)"
                    >
                        <div>{{ item.name }}</div>
                        <div class="step-details">编号: {{ item.no }} | 负责人: {{ item.member }}</div>
                    </div>
                </div>
                
                <div class="mode-selector">
                    <h4>操作模式</h4>
                    <button 
                        :class="['mode-btn', { active: currentMode === 'select' }]" 
                        @click="currentMode = 'select'"
                    >
                        选择模式
                    </button>
                    <button 
                        :class="['mode-btn', { active: currentMode === 'connect' }]" 
                        @click="currentMode = 'connect'"
                    >
                        连接模式
                    </button>
                </div>
                
                <div v-if="currentMode === 'connect'" class="flow-type-selector">
                    <div 
                        :class="['flow-type-btn', { active: flowType === 'serial' }]"
                        @click="flowType = 'serial'"
                    >
                        串行流程
                    </div>
                    <div 
                        :class="['flow-type-btn', { active: flowType === 'parallel' }]"
                        @click="flowType = 'parallel'"
                    >
                        并行流程
                    </div>
                </div>
                
                <div class="controls">
                    <button @click="previewData">数据预览</button>
                    <button @click="clearCanvas" :disabled="!processSteps.length">清空画布</button>
                    <button @click="submitDesign" :disabled="!processSteps.length">提交设计</button>
                </div>
                
                <div v-if="message" :class="['status', messageType]">
                    {{ message }}
                </div>
            </div>
            
            <!-- 右侧画布区域 -->
            <div class="canvas-container" ref="canvas" @drop="onDrop" @dragover="allowDrop" @click="onCanvasClick">
                <!-- 绘制连接线 -->
                <div 
                    v-for="(connection, index) in connections" 
                    :key="index"
                    class="connection-line"
                    :class="connection.type"
                    :style="getConnectionStyle(connection)"
                ></div>
                
                <!-- 工艺步骤 -->
                <div 
                    v-for="(step, index) in processSteps" 
                    :key="step.id"
                    class="process-step"
                    :class="{ selected: selectedStepId === step.id && currentMode === 'connect' }"
                    :style="{ left: step.x + 'px', top: step.y + 'px' }"
                    @mousedown="startDrag($event, index)"
                    @touchstart="startDrag($event, index)"
                    @click.stop="onStepClick(step.id, $event)"
                >
                    <div>{{ step.name }}</div>
                    <div class="step-details">编号: {{ step.no }} | 负责人: {{ step.member }}</div>
                    <div class="step-details">ID: {{ step.id }}</div>
                    
                    <!-- 连接点 -->
                    <div 
                        v-if="currentMode === 'connect'" 
                        class="connection-point input"
                        @click.stop="onConnectionPointClick(step.id, 'input', $event)"
                    ></div>
                    <div 
                        v-if="currentMode === 'connect'" 
                        class="connection-point output"
                        @click.stop="onConnectionPointClick(step.id, 'output', $event)"
                    ></div>
                </div>
            </div>
        </div>
        
        <!-- 数据预览弹窗 -->
        <div v-if="showPreviewModal" class="modal">
            <div class="modal-content">
                <span class="close" @click="showPreviewModal = false">&times;</span>
                <h3>数据预览</h3>
                <div class="json-output">{{ formattedOutputData }}</div>
            </div>
        </div>
    </div>
</template>

<script>
// 导入Vue、Axios等
import axios from 'axios';
import UserInfoUtils from '@/utils/userInfoUtils.js';

export default {
  name: 'ProcessDesign',
  data() {
    return {
        processData: [],
        processSteps: [],
        connections: [],
        loading: true,
        message: '',
        messageType: '',
        draggingIndex: null,
        offsetX: 0,
        offsetY: 0,
        currentMode: 'select', // 'select' or 'connect'
        flowType: 'serial', // 'serial' or 'parallel'
        selectedStepId: null,
        connectingFrom: null,
        connectingTo: null,
        showPreviewModal: false,
        outputData: {}
    };
  },
  computed: {
    formattedOutputData() {
        return JSON.stringify(this.outputData, null, 2);
    }
  },
  mounted() {
    // 使用您提供的测试数据
    this.processData = [
        {
            "name": "焊",
            "no": 1,
            "member": "李勇"
        },
        {
            "name": "拼装",
            "no": 2,
            "member": "刘永杰"
        },
        {
            "name": "拼装",
            "no": 3,
            "member": "陶伟"
        },
        {
            "name": "车",
            "no": 4,
            "member": "王辉"
        },
        {
            "name": "铣",
            "no": 5,
            "member": "李强"
        }
    ];
    this.loading = false;
    this.showMessage('工艺基础数据加载成功', 'success');

    // 初始化画布大小
    this.updateCanvasSize();
    window.addEventListener('resize', this.updateCanvasSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateCanvasSize);
  },
  methods: {
    /**
     * 返回首页
     */
    backHome() {
      // 返回主页：按权限决定（有项目管理权限→项目主页，否则→合同主页），避免跳转到无权限的项目页
      this.$router.push(UserInfoUtils.getDefaultHomeRoute());
    },

    updateCanvasSize() {
        // 动态调整容器高度以适应浏览器窗口
        const container = document.querySelector('.container');
        if (container) {
            container.style.height = `calc(100vh - 120px)`;
        }
    },
    onDragStart(event, item) {
        event.dataTransfer.setData('text/plain', JSON.stringify(item));
    },
    allowDrop(event) {
        event.preventDefault();
    },
    onDrop(event) {
        event.preventDefault();
        const itemData = event.dataTransfer.getData('text/plain');
        if (itemData) {
            const item = JSON.parse(itemData);
            const rect = this.$refs.canvas.getBoundingClientRect();
            const x = event.clientX - rect.left - 60; // 中心对齐
            const y = event.clientY - rect.top - 30;
            
            this.processSteps.push({
                id: Date.now(),
                name: item.name,
                no: item.no,
                member: item.member,
                x: Math.max(0, x),
                y: Math.max(0, y)
            });
        }
    },
    // startDrag(event, index) {
    //     if (this.currentMode !== 'select') return;
        
    //     this.draggingIndex = index;
    //     const step = this.processSteps[index];
        
    //     // 计算鼠标相对于元素的位置
    //     this.offsetX = event.clientX - (step.x + 60); // 假设元素宽度的一半
    //     this.offsetY = event.clientY - (step.y + 25); // 假设元素高度的一半
        
    //     const moveHandler = (e) => {
    //         if (this.draggingIndex !== null) {
    //             const canvasRect = this.$refs.canvas.getBoundingClientRect();
    //             let newX = e.clientX - canvasRect.left - this.offsetX;
    //             let newY = e.clientY - canvasRect.top - this.offsetY;
                
    //             // 限制在画布范围内
    //             newX = Math.max(0, Math.min(newX, canvasRect.width - 120));
    //             newY = Math.max(0, Math.min(newY, canvasRect.height - 70));
                
    //             this.$set(this.processSteps, index, {
    //                 ...this.processSteps[index],
    //                 x: newX,
    //                 y: newY
    //             });
    //         }
    //     };
        
    //     const upHandler = () => {
    //         this.draggingIndex = null;
    //         document.removeEventListener('mousemove', moveHandler);
    //         document.removeEventListener('mouseup', upHandler);
    //         document.removeEventListener('touchmove', moveHandler);
    //         document.removeEventListener('touchend', upHandler);
    //     };
        
    //     document.addEventListener('mousemove', moveHandler);
    //     document.addEventListener('mouseup', upHandler);
    //     document.addEventListener('touchmove', moveHandler);
    //     document.addEventListener('touchend', upHandler);
    // },

    startDrag(event, index) {
        if (this.currentMode !== 'select') return;
        
        this.draggingIndex = index;
        const step = this.processSteps[index];
        
        // 获取实际的元素
        const canvas = this.$refs.canvas;
        const elements = canvas.querySelectorAll('.process-step');
        const currentElement = elements[index];
        
        if (currentElement) {
            // 获取元素的真实尺寸
            const rect = currentElement.getBoundingClientRect();
            
            // 计算鼠标在元素内部的位置
            this.offsetX = event.clientX - rect.left;
            this.offsetY = event.clientY - rect.top;
        } else {
            // 回退方案：使用中心点
            this.offsetX = 60;  // 元素宽度的一半
            this.offsetY = 25;  // 元素高度的一半
        }
        
        const moveHandler = (e) => {
            if (this.draggingIndex !== null) {
                const canvasRect = this.$refs.canvas.getBoundingClientRect();
                let newX = e.clientX - canvasRect.left - this.offsetX;
                let newY = e.clientY - canvasRect.top - this.offsetY;
                
                // 限制在画布范围内
                newX = Math.max(0, Math.min(newX, canvasRect.width - 120));
                newY = Math.max(0, Math.min(newY, canvasRect.height - 70));
                
                this.$set(this.processSteps, index, {
                    ...this.processSteps[index],
                    x: newX,
                    y: newY
                });
            }
        };
        
        const upHandler = () => {
            this.draggingIndex = null;
            document.removeEventListener('mousemove', moveHandler);
            document.removeEventListener('mouseup', upHandler);
            document.removeEventListener('touchmove', moveHandler);
            document.removeEventListener('touchend', upHandler);
        };
        
        document.addEventListener('mousemove', moveHandler);
        document.addEventListener('mouseup', upHandler);
        document.addEventListener('touchmove', moveHandler);
        document.addEventListener('touchend', upHandler);
    },


    onStepClick(stepId, event) {
        if (this.currentMode === 'connect') {
            if (!this.connectingFrom) {
                this.connectingFrom = stepId;
                this.selectedStepId = stepId;
            } else if (this.connectingFrom !== stepId) {
                this.connectingTo = stepId;
                this.addConnection();
                this.resetConnectionState();
            }
        }
    },
    onConnectionPointClick(stepId, pointType, event) {
        event.stopPropagation();
        if (this.currentMode === 'connect') {
            if (!this.connectingFrom) {
                this.connectingFrom = stepId;
                this.selectedStepId = stepId;
            } else if (this.connectingFrom !== stepId) {
                this.connectingTo = stepId;
                this.addConnection();
                this.resetConnectionState();
            }
        }
    },
    addConnection() {
        if (this.connectingFrom && this.connectingTo) {
            // 检查是否已存在相同的连接
            const exists = this.connections.some(conn => 
                conn.from === this.connectingFrom && conn.to === this.connectingTo
            );
            
            if (!exists) {
                this.connections.push({
                    from: this.connectingFrom,
                    to: this.connectingTo,
                    type: this.flowType
                });
            }
        }
    },
    resetConnectionState() {
        this.connectingFrom = null;
        this.connectingTo = null;
        this.selectedStepId = null;
    },
    getConnectionStyle(connection) {
        const fromStep = this.processSteps.find(s => s.id === connection.from);
        const toStep = this.processSteps.find(s => s.id === connection.to);
        
        if (!fromStep || !toStep) return {};
        
        // 计算起点和终点坐标（使用连接点位置）
        const startX = fromStep.x + 120; // 右侧中点
        const startY = fromStep.y + 25;  // 垂直中点
        const endX = toStep.x;           // 左侧中点
        const endY = toStep.y + 25;      // 垂直中点
        
        // 计算线的长度和角度
        const length = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
        const angle = Math.atan2(endY - startY, endX - startX) * 180 / Math.PI;
        
        return {
            width: length + 'px',
            height: connection.type === 'parallel' ? '4px' : '2px',
            left: startX + 'px',
            top: startY + 'px',
            transform: `rotate(${angle}deg)`,
            transformOrigin: '0 0'
        };
    },
    onCanvasClick(event) {
        // 点击画布空白处取消选择
        if (event.target.classList.contains('canvas-container')) {
            this.selectedStepId = null;
            this.resetConnectionState();
        }
    },
    clearCanvas() {
        this.processSteps = [];
        this.connections = [];
        this.selectedStepId = null;
        this.showMessage('画布已清空', 'success');
    },
    previewData() {
        this.outputData = {
            steps: this.processSteps.map(step => ({
                id: step.id,
                name: step.name,
                no: step.no,
                member: step.member,
                position: { x: step.x, y: step.y }
            })),
            connections: this.connections.map(conn => ({
                from: conn.from,
                to: conn.to,
                type: conn.type
            })),
            timestamp: new Date().toISOString()
        };
        this.showPreviewModal = true;
    },
    async submitDesign() {
        if (!this.processSteps.length) {
            this.showMessage('请先在画布上添加工艺步骤', 'error');
            return;
        }
        
        try {
            const designData = {
                steps: this.processSteps.map(step => ({
                    id: step.id,
                    name: step.name,
                    no: step.no,
                    member: step.member,
                    position: { x: step.x, y: step.y }
                })),
                connections: this.connections.map(conn => ({
                    from: conn.from,
                    to: conn.to,
                    type: conn.type
                })),
                timestamp: new Date().toISOString()
            };
            
            // 这里调用实际的B接口
            const response = await axios.post('http://localhost:8090/testPostApi', designData);
            this.showMessage('设计提交成功', 'success');
            console.log('提交的数据:', designData);
            console.log('服务器响应:', response.data);
        } catch (error) {
            console.error('提交失败:', error);
            this.showMessage('提交失败: ' + error.message, 'error');
        }
    },
    showMessage(msg, type) {
        this.message = msg;
        this.messageType = type;
        setTimeout(() => {
            this.message = '';
        }, 3000);
    }
  }
        
};
</script>

<style>

    /* 标题行样式 - 包含标题和返回按钮 */
    .header-row {
    display: flex;
    justify-content: space-between; /* 保持空间分布 */
    align-items: center;
    margin-bottom: 10px;
    margin-top: 10px;
    position: relative; /* 相对定位 */
    }

    /* 标题居中容器 */
    .header-center {
    position: absolute; /* 绝对定位 */
    left: 50%; /* 左侧定位到中间 */
    transform: translateX(-50%); /* 向左移动自身宽度的50%实现居中 */
    text-align: center; /* 文字居中 */
    flex: 1; /* 占用可用空间 */
    }

    /* 返回首页按钮样式 */
    .backhome-icon {
    font-size: 28px; /* 与DashboardRightAlarm.vue保持一致 */
    color: #2196f3; /* 主色调蓝色 */
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 8px;
    border-radius: 50%;
    margin-left: auto; /* 按钮靠右 */
    z-index: 2; /* 确保在标题上方 */
    position: relative; /* 相对定位确保z-index生效 */
    }
    body {
        font-family: Arial, sans-serif;
        margin: 0;
        /* padding: 20px; */
        background-color: #f5f5f5;
        overflow-x: hidden;
    }
    .container {
        display: flex;
        height: calc(100vh - 120px);
        gap: 20px;
    }
    .sidebar {
        width: 300px;
        background: white;
        border-radius: 8px;
        padding: 15px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        overflow-y: auto;
    }
    .canvas-container {
        flex: 1;
        background: white;
        border-radius: 8px;
        padding: 15px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        position: relative;
        overflow: auto;
        min-height: 600px;
    }
    .process-item {
        padding: 10px;
        margin: 5px 0;
        background-color: #e3f2fd;
        border-radius: 4px;
        cursor: grab;
        user-select: none;
    }
    .process-item:hover {
        background-color: #bbdefb;
    }
    .process-step {
        position: absolute;
        padding: 10px;
        background-color: #c8e6c9;
        border-radius: 4px;
        cursor: move;
        z-index: 10;
        min-width: 120px;
        text-align: center;
        border: 2px solid #4caf50;
    }
    .process-step.selected {
        border: 2px solid #ff9800;
        box-shadow: 0 0 10px rgba(255, 152, 0, 0.5);
    }
    .process-step.dragging {
        opacity: 0.7;
    }
    .controls {
        margin-top: 15px;
    }
    button {
        /* 就是这个里面的按钮设置padding导致有的地方符号过宽 */
        /* padding: 10px 15px; */
        background-color: #2196f3;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        margin-right: 10px;
        margin-bottom: 10px;
    }
    button:hover {
        background-color: #1976d2;
    }
    button:disabled {
        background-color: #bbdefb;
        cursor: not-allowed;
    }
    .connection-line {
        position: absolute;
        background-color: #9e9e9e;
        z-index: 5;
        transform-origin: 0 0;
    }
    .connection-line.parallel {
        background-color: #ff9800;
        height: 4px !important;
    }
    .connection-line.serial {
        background-color: #4caf50;
        height: 2px !important;
    }
    .status {
        margin-top: 10px;
        padding: 10px;
        border-radius: 4px;
    }
    .success {
        background-color: #e8f5e8;
        color: #2e7d32;
    }
    .error {
        background-color: #ffebee;
        color: #c62828;
    }
    .step-details {
        font-size: 12px;
        color: #666;
        margin-top: 5px;
    }
    .mode-selector {
        margin: 10px 0;
        padding: 10px;
        background-color: #f0f0f0;
        border-radius: 4px;
    }
    .mode-btn {
        background-color: #9e9e9e;
    }
    .mode-btn.active {
        background-color: #4caf50;
        color: white;
    }
    .flow-type-selector {
        display: flex;
        gap: 10px;
        margin-top: 10px;
    }
    .flow-type-btn {
        flex: 1;
        text-align: center;
        padding: 8px;
        background-color: #e0e0e0;
        border-radius: 4px;
        cursor: pointer;
    }
    .flow-type-btn.active {
        background-color: #2196f3;
        color: white;
    }
    .connection-point {
        position: absolute;
        width: 10px;
        height: 10px;
        background-color: #ff5722;
        border-radius: 50%;
        z-index: 20;
        cursor: pointer;
    }
    .connection-point.output {
        right: -5px;
        top: 50%;
        transform: translateY(-50%);
    }
    .connection-point.input {
        left: -5px;
        top: 50%;
        transform: translateY(-50%);
    }
    
    /* 弹窗样式 */
    .modal {
        display: block;
        position: fixed;
        z-index: 1000;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0,0,0,0.5);
    }
    .modal-content {
        background-color: white;
        margin: 5% auto;
        padding: 20px;
        border-radius: 8px;
        width: 80%;
        max-width: 800px;
        max-height: 80vh;
        overflow-y: auto;
    }
    .close {
        color: #aaa;
        float: right;
        font-size: 28px;
        font-weight: bold;
        cursor: pointer;
    }
    .close:hover {
        color: black;
    }
    .json-output {
        background-color: #f5f5f5;
        padding: 15px;
        border-radius: 4px;
        font-family: monospace;
        white-space: pre-wrap;
        word-break: break-all;
    }
</style>

